import React, { useState } from 'react';
import { useSettings } from '../../context/SettingsContext';
import { useCalculations } from '../../context/CalculationContext';
import { useUser } from '../../context/UserContext';
import useUserData from '../../hooks/useUserData';
import SectionHeader from '../common/SectionHeader';
import SectionNavigation from '../common/SectionNavigation';
import KeyMetricsSummary from './KeyMetricsSummary';
import ResultsChart from './ResultsChart';
import ResultsTable from './ResultsTable';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import SaveIcon from '@mui/icons-material/Save';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';

function ResultsSection({ onNextSection, onPreviousSection, currentSectionIndex, sectionNames }) {
  const { expandedSections, visibilitySettings, globalSettings, handleGlobalSettingChange } = useSettings();
  const { isCalculating } = useCalculations(); // Get loading state
  const { user } = useUser(); // Get current user state
  const { isLoading, lastSaved, error, saveData } = useUserData(); // Get Firebase save functionality
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Basic handlers for the settings - reuse logic from GlobalSettingsForm if needed
  const handleCheckboxChange = (field, checked) => {
    handleGlobalSettingChange(field, checked);
  };
  
  // Handle opening the save dialog
  const handleOpenSaveDialog = () => {
    setSaveDialogOpen(true);
  };
  
  // Handle closing the save dialog
  const handleCloseSaveDialog = () => {
    setSaveDialogOpen(false);
  };
  
  // Handle saving the scenario using Firebase
  const handleSaveScenario = async () => {
    try {
      await saveData(); // Use the same Firebase save function as upper button
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000); // Hide after 3 seconds
    } catch (err) {
      console.error('Save failed:', err);
      // Error handling is already done in useUserData hook
    }
  };

  return (
    <div className="calculator-section results-section">
      <SectionHeader title="Projected Results" sectionKey="results" />

      <div style={{ position: 'relative' }}> {/* Add relative positioning for loading overlay */}

           {/* Loading Indicator (Optional - can be inside chart/table too) */}
           {/* {isCalculating && <div className="loading-overlay">Calculating...</div>} */}

          <KeyMetricsSummary /> {/* Key Metrics component */}

          <ResultsChart /> {/* This contains the ChartDisplayOptions component */}
          
          {/* Save Button for scenarios */}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            {saveSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Your scenario has been saved successfully!
              </Alert>
            )}
            
            {/* Show save button if user is Guest or not logged in */}
            {(!user || user.isGuest) ? (
              <Button 
                variant="contained" 
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleOpenSaveDialog}
                sx={{ mb: 2 }}
              >
                Save This Scenario
              </Button>
            ) : (
              <Button 
                variant="contained" 
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSaveScenario}
                sx={{ mb: 2 }}
              >
                Save Scenario
              </Button>
            )}
          </Box>
        </div>
        
        {/* Login Dialog */}
        <Dialog open={saveDialogOpen} onClose={handleCloseSaveDialog}>
          <DialogTitle>Sign In to Save</DialogTitle>
          <DialogContent>
            <DialogContentText>
              You need to sign in or create an account to save your scenarios. 
              Sign in using the button in the top right corner of the page.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseSaveDialog}>Close</Button>
          </DialogActions>
        </Dialog>
        
      <SectionNavigation
        onNextSection={onNextSection}
        onPreviousSection={onPreviousSection}
        currentSectionIndex={currentSectionIndex}
        sectionNames={sectionNames}
      />
    </div>
  );
}

export default ResultsSection;